import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLibraryStore } from '../store/library';
import { AssistantFab } from './AssistantFab';
import { AssistantPanel, isNarrowScreen } from './AssistantPanel';
import { examplesFor, pageHint, refreshAfter } from './assistantPage';

/** History-state marker for the phone sheet, so the system back button closes it. */
const SHEET_STATE_KEY = 'cstAssistantSheet';
const sheetOnTop = () => !!(window.history.state as Record<string, unknown> | null)?.[SHEET_STATE_KEY];

/** App-wide assistant: one FAB + panel on every authenticated page. */
export function AssistantHost() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const page = pageHint(location.pathname, location.search);
  const fabRef = useRef<HTMLButtonElement>(null);
  /** True while the phone sheet owns the top history entry. */
  const sheetEntry = useRef(false);

  const close = useCallback(() => {
    setOpen(false);
    if (sheetEntry.current) {
      sheetEntry.current = false;
      if (sheetOnTop()) window.history.back();
    }
    // Return focus to the entry point once it is visible again.
    requestAnimationFrame(() => fabRef.current?.focus());
  }, []);

  // Phone sheet: push a same-URL entry (keeping the router's state) so back closes it.
  useEffect(() => {
    if (!open || !isNarrowScreen()) return;
    window.history.pushState({ ...((window.history.state as object | null) ?? {}), [SHEET_STATE_KEY]: true }, '');
    sheetEntry.current = true;
    const onPop = () => {
      if (sheetEntry.current && !sheetOnTop()) { sheetEntry.current = false; setOpen(false); }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [open]);

  const go = useCallback((path: string) => {
    if (isNarrowScreen()) {
      // The sheet would cover the page just opened. Leave its entry behind; the
      // router pushes the new page after it.
      sheetEntry.current = false;
      setOpen(false);
    }
    navigate(path);
    // HashRouter navigates with pushState, which fires no hashchange; pages that
    // read the hash query (스케줄 > 승인 관리) listen for it.
    window.dispatchEvent(new Event('hashchange'));
  }, [navigate]);

  return (
    <>
      <AssistantFab ref={fabRef} open={open} onToggle={() => (open ? close() : setOpen(true))} />
      <AssistantPanel
        open={open}
        page={page}
        examples={examplesFor(page)}
        onClose={close}
        onNavigate={go}
        onApplied={(steps) => refreshAfter(steps, useLibraryStore.getState())}
      />
    </>
  );
}
